import { memo, useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { getSportLinks } from '../../config/sportLinks';

/**
 * UtilityBanner - Full-width banner with sport quick links and status indicators
 *
 * @param {Object} props
 * @param {Object} props.user - Current user object
 * @param {Object} props.starCoins - Star coins data with balance and loading state
 * @param {string} props.tier - User's subscription tier (e.g., 'pro', 'free')
 * @param {Function} props.t - Translation function for internationalization
 * @param {Function} props.showAuthPrompt - Function to show authentication prompt
 */
const UtilityBanner = ({ user, starCoins, tier, t, showAuthPrompt }) => {
  const navigate = useNavigate();
  const [isDesktop, setIsDesktop] = useState(window.innerWidth > 768);

  useEffect(() => {
    const handleResize = () => {
      setIsDesktop(window.innerWidth > 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Get sport links from shared configuration
  const sportLinks = getSportLinks();

  const handleStarCoinsClick = () => {
    console.log('Star coin pill clicked, user:', user);
    if (user) {
      console.log('Dispatching openStarCoinsModal event');
      window.dispatchEvent(new CustomEvent('openStarCoinsModal'));
    } else {
      showAuthPrompt('starCoins');
    }
  };

  const handleHelpClick = () => {
    window.dispatchEvent(new CustomEvent('startOnboarding'));
  };

  const handleSportClick = (sport) => {
    if (sport.status === 'coming-soon') {
      navigate('/chat');
    } else {
      navigate(sport.path);
    }
  };

  // Only show on desktop
  if (!isDesktop) {
    return null;
  }

  return (
    <>
      {/* Professional Terminal Utility Banner */}
      <div className="fixed top-0 left-0 right-0 z-20 bg-[#0d0d0d] border-b border-[#2a2a2a]">
        {/* Centered container matching homepage content width */}
        <div className="max-w-[1280px] mx-auto px-4 md:px-6">
          <div className="flex items-center gap-6 py-2.5">
            {/* Left Side - Brand + Sport Links */}
            <div className="flex items-center gap-4 overflow-x-auto scrollbar-hide flex-1 min-w-0">
              {/* Brand Label */}
              <div className="flex items-center gap-3 flex-shrink-0">
              <span className="text-[#4ade80] text-sm font-semibold tracking-tight">
                THE PICK LAB →
              </span>
              <div className="h-4 w-px bg-[#2a2a2a]"></div>
            </div>

            {/* Sport Buttons */}
            {sportLinks.map((sport) => {
              const isActive = sport.status === 'active';
              const isComingSoon = sport.status === 'coming-soon';

              return (
                <button
                  key={sport.id}
                  onClick={() => handleSportClick(sport)}
                  disabled={!isActive && !isComingSoon}
                  className={`px-3 py-1 transition-all text-xs font-medium whitespace-nowrap flex-shrink-0
                    ${isActive
                      ? 'text-[#a3a3a3] hover:text-white'
                      : isComingSoon
                      ? 'text-[#737373] hover:text-[#a3a3a3]'
                      : 'text-[#404040] cursor-not-allowed'}`}
                >
                  {sport.label}
                </button>
              );
            })}
            </div>

              {/* Right Side - Status Indicators */}
            <div className="flex items-center gap-4 flex-shrink-0">
              {/* Star Coins */}
              {user?.uid && !starCoins?.loading && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="flex items-center gap-2 cursor-pointer hover:text-white transition-colors"
                  onClick={handleStarCoinsClick}
                >
                  <span className="text-[#d4af37] text-xs font-medium tabular-nums">
                    {starCoins?.balance || 0}
                  </span>
                  <span className="text-[#d4af37] text-xs">StarCoins</span>
                </motion.div>
              )}

              {/* Tier */}
              {tier && (
                <div className="flex items-center gap-2">
                  <span className="text-[#a3a3a3] text-xs font-medium">
                    {t(tier === 'pro' ? 'PRO' : 'FREE')}
                  </span>
                </div>
              )}

              {/* Help */}
              <motion.button
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                onClick={handleHelpClick}
                className="text-[#737373] hover:text-[#a3a3a3] text-xs font-medium transition-colors"
              >
                {t('Tour')}
              </motion.button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

// Memoize with custom comparison to prevent unnecessary re-renders
export default memo(UtilityBanner, (prevProps, nextProps) => {
  return (
    prevProps.user?.uid === nextProps.user?.uid &&
    prevProps.starCoins?.loading === nextProps.starCoins?.loading &&
    prevProps.starCoins?.balance === nextProps.starCoins?.balance &&
    prevProps.tier === nextProps.tier
    // Note: t and showAuthPrompt are assumed to be stable functions
  );
});
